'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LcmCalculator() {
  const t = useTranslations("tools.lcmCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numbersInput, setNumbersInput] = useState('12, 18, 24');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const gcd = (a: number, b: number): number => {
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    return a;
  };

  const lcm = (a: number, b: number): number => {
    return Math.abs(a * b) / gcd(a, b);
  };

  const lcmMultiple = (numbers: number[]): number => {
    return numbers.reduce((acc, num) => lcm(acc, num));
  };

  const primeFactorization = (n: number): { [key: number]: number } => {
    const factors: { [key: number]: number } = {};
    let num = n;
    let divisor = 2;

    while (num > 1) {
      while (num % divisor === 0) {
        factors[divisor] = (factors[divisor] || 0) + 1;
        num = num / divisor;
      }
      divisor++;
      if (divisor * divisor > num && num > 1) {
        factors[num] = (factors[num] || 0) + 1;
        break;
      }
    }

    return factors;
  };

  const formatFactorization = (factors: { [key: number]: number }): string => {
    return Object.entries(factors)
      .map(([prime, power]) => power > 1 ? `${prime}^${power}` : prime)
      .join(' × ');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const input = numbersInput.trim();
    const numberStrings = input.split(',').map(s => s.trim()).filter(s => s);

    if (numberStrings.length < 2) {
      setError(t('errorMinNumbers'));
      return;
    }

    try {
      const numbers = numberStrings.map(s => {
        const num = parseInt(s, 10);
        if (isNaN(num) || num <= 0) {
          throw new Error('Invalid number');
        }
        return num;
      });

      if (numbers.some(n => n > 1000000)) {
        setError(t('errorMaxValue'));
        return;
      }

      const result = lcmMultiple(numbers);

      const factorizations = numbers.map(num => ({
        number: num,
        factors: primeFactorization(num),
        formatted: formatFactorization(primeFactorization(num))
      }));

      const resultFactors = primeFactorization(result);
      const resultFormatted = formatFactorization(resultFactors);

      setResults({
        lcm: result,
        numbers: numbers,
        factorizations: factorizations,
        resultFactorization: {
          factors: resultFactors,
          formatted: resultFormatted
        }
      });

    } catch (err) {
      console.error(err);
      setError(t('errorInvalidInput'));
    }
  };

  const handleClear = () => {
    setNumbersInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem', borderRadius: '1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', monospace",
      resize: 'vertical' as const,
      minHeight: '120px',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.825rem', fontSize: '0.975rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const inputHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem', display: 'block' },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      padding: '1rem',
      background: '#fef2f2',
      borderLeft: '4px solid #ef4444',
      borderRadius: '0.5rem',
      color: '#991b1b',
      marginTop: '1rem',
      display: error ? 'block' : 'none'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #22c55e',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#15803d', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.1875rem' },
    { fontSize: '1.25rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569', display: 'block', marginBottom: '0.5rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', display: 'block' },
    { fontSize: '1.75rem' },
    { fontSize: '1.875rem' },
    { fontSize: '2rem' }
  );

  const calculationStepsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const stepsH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: colors.textPrimary },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const stepItemStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      background: '#f8fafc',
      borderLeft: '3px solid #7c3aed',
      marginBottom: '0.75rem',
      borderRadius: '0.25rem',
      fontFamily: "'Courier New', monospace"
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const factorizationItemStyle = getResponsiveStyle(
    {
      padding: '0.5rem 0',
      borderBottom: '1px solid #e2e8f0',
      fontFamily: "'Courier New', monospace"
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem', borderRadius: '1rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem', lineHeight: 1.6 },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  const referenceItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none' as const,
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🔢 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="numbers-input">
                {t('inputLabel')}
              </label>
              <textarea
                id="numbers-input"
                style={textareaStyle}
                placeholder={t('inputPlaceholder')}
                value={numbersInput}
                onChange={(e) => setNumbersInput(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
              <span style={inputHintStyle}>{t('inputHint')}</span>
            </div>

            <div style={errorMessageStyle}>
              {error}
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearButtonStyle}
                onClick={handleClear}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                }}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>✨ {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <span style={resultLabelStyle}>{t('resultLabel')}</span>
                <span style={resultValueStyle}>{results.lcm.toLocaleString()}</span>
              </div>

              <div style={calculationStepsStyle}>
                <h4 style={stepsH4Style}>{t('primeFactorizationTitle')}</h4>
                <div>
                  {results.factorizations.map((item: any, index: number) => (
                    <div key={index} style={factorizationItemStyle}>
                      {item.number} = {item.formatted}
                    </div>
                  ))}
                  <div style={{...factorizationItemStyle, fontWeight: 'bold', color: '#2563eb', borderBottom: 'none'}}>
                    LCM = {results.resultFactorization.formatted} = {results.lcm.toLocaleString()}
                  </div>
                </div>
              </div>

              <div style={calculationStepsStyle}>
                <h4 style={stepsH4Style}>{t('calculationMethodTitle')}</h4>
                <div>
                  <div style={stepItemStyle}>{t('step1')}</div>
                  <div style={stepItemStyle}>{t('step2')}</div>
                  <div style={stepItemStyle}>{t('step3')}</div>
                  <div style={stepItemStyle}>{t('step4')} {results.lcm.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('insightsMethodsTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsPrimeFactorization')}</p>
        <p style={paragraphStyle}>{t('insightsGcdMethod')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('insightsBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsAccuracy')}</li>
          <li style={listItemStyle}>{t('insightsMethodSelection')}</li>
          <li style={listItemStyle}>{t('insightsEducationalUse')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={{...listStyle, listStyleType: 'none', marginLeft: 0}}>
          <li style={referenceItemStyle}>
            <a href="https://en.wikipedia.org/wiki/Least_common_multiple" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.calculator.net/lcm-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.mathsisfun.com/least-common-multiple.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.calculatorsoup.com/calculators/math/lcm.php" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.smartick.com/lp/learn-least-common-multiple-lcm/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference5')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.khanacademy.org/math/cc-sixth-grade-math/cc-6th-expressions-and-variables/cc-6th-lcm/v/least-common-multiple-exercise" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference6')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://math.libretexts.org/Courses/Mount_Royal_University/Higher_Arithmetic/4:_Greatest_Common_Divisor_least_common_multiple_and_Euclidean_Algorithm/4.3:_Least_Common_Multiple" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference7')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.geeksforgeeks.org/maths/lcm-least-common-multiple/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference8')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}